import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  Length,
  MarketAvailability,
  MoneyRange,
  PlatformProduct,
} from '~/graphql/graphql';
import { getPropertyStringSafe } from '~/utils/properties.tsx';
import { useGetProductPropertyKeysWithValues } from '~/api/product-properties';
import { PropertyRow } from '~/components/cards/PropertyRow';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import { getPlatformProductDimensions } from '~/utils/order';
import {
  convertPricePerUnit,
  pricedUnitFor,
  toStandardUnits,
} from '~/utils/unitConversion';
import { formatCurrency, priceUnitLabel } from '~/utils';

const BLANK = '—';

interface UniqueProductsCardProps {
  platformProduct: PlatformProduct;
  marketAvailability: MarketAvailability | null;
  landedPriceRange?: MoneyRange | null;
  onClick: () => void;
  lengths: Length[];
}

export function UniqueProductsCard({
  platformProduct,
  marketAvailability,
  landedPriceRange,
  lengths,
  onClick,
}: UniqueProductsCardProps) {
  const { t } = useTranslation('product');
  const { data: keyData } = useGetProductPropertyKeysWithValues(
    platformProduct.category
  );

  const stockUnit =
    marketAvailability?.totalStock.unit ?? platformProduct.standardUnitsCode;
  const displayUnit = usePreferredUnit(platformProduct.category, stockUnit);
  const noPplBasis = undefined;
  const dims = getPlatformProductDimensions(
    platformProduct,
    lengths[0] ?? null,
    noPplBasis
  );
  const shownUnit = pricedUnitFor(displayUnit, stockUnit, dims);

  const stockValue = marketAvailability?.totalStock.value ?? 0;
  const displayTotalStock =
    shownUnit === stockUnit
      ? stockValue
      : toStandardUnits(
          stockValue,
          stockUnit,
          displayUnit,
          dims.lengthInches,
          dims.widthInches,
          dims.thicknessInches
        );

  const sellersCount = marketAvailability?.sellersCount ?? 0;
  const listingsCount = marketAvailability?.listingsCount ?? 0;
  const hasStock = marketAvailability !== null && stockValue > 0;
  const displayPriceMin = landedPriceRange?.min ?? marketAvailability?.priceMin;
  const displayPriceMax = landedPriceRange?.max ?? marketAvailability?.priceMax;

  const propertyRows = (keyData?.productPropertyKeysWithValues ?? [])
    .map(({ key, displayName, values }) => {
      const rawValue = getPropertyStringSafe(platformProduct.properties, key);
      const displayValue =
        values.find((v) => v.value === rawValue)?.displayValue ?? rawValue;
      return { key, label: displayName, displayValue, rawValue };
    })
    .filter(({ rawValue }) => rawValue !== '-')
    .slice(0, 3);

  return (
    <div
      data-testid="unique-products-card"
      className="bg-white rounded-lg border border-gray-200 hover:border-prim hover:shadow-lg transition-all shadow-sm flex flex-col"
    >
      <div className="cursor-pointer flex-1 flex flex-col">
        <div className="h-48 bg-gradient-to-br from-amber-50 to-amber-100 rounded-t-lg flex items-center justify-center">
          <div className="text-4xl">🌲</div>
        </div>
        <div className="p-4 sm:p-6 flex-1 flex flex-col">
          <div className="flex-1">
            <div className="mb-3">
              <h3 className="text-neut-900 mb-1 font-semibold line-clamp-2">
                {platformProduct.name}
              </h3>
              <p className="text-xs text-neut-500 font-normal">
                {marketAvailability !== null
                  ? t('product.sellers_listings', {
                      sellersCount,
                      listingsCount,
                    })
                  : BLANK}
              </p>
            </div>
            <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
              {propertyRows.map(({ key, label, displayValue }) => (
                <PropertyRow
                  key={key}
                  propertyKey={key}
                  displayName={label}
                  displayValue={displayValue}
                />
              ))}
              <div className="flex items-start gap-1.5">
                <span className="text-xs text-gray-600 font-normal shrink-0">
                  {t('product.lengths')}:
                </span>
                <div className="flex flex-wrap gap-1">
                  {lengths?.map((length, index) => (
                    <span
                      key={index}
                      className="text-xs px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium"
                    >
                      {length?.displayValue}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-gray-100">
              <div>
                <p className="text-xs text-gray-500 font-normal mb-1">
                  {t('product.total_stock')}
                </p>
                <p
                  data-testid="product-total-stock"
                  className="text-sm text-gray-900 font-semibold"
                >
                  {hasStock ? `${displayTotalStock} ${shownUnit}` : BLANK}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-normal mb-1">
                  {t('product.lead_time')}
                </p>
                <p className="text-sm text-gray-900 font-semibold">
                  {marketAvailability !== null
                    ? `${getLeadTime(marketAvailability.leadTimeMin, marketAvailability.leadTimeMax)} ${t('product.days')}`
                    : BLANK}
                </p>
              </div>
            </div>
            <div>
              {marketAvailability !== null &&
              displayPriceMin != null &&
              displayPriceMax != null ? (
                <div className="flex flex-wrap items-baseline gap-1">
                  <span className="text-2xl text-neut-900 font-bold whitespace-nowrap">
                    {formatCurrency(
                      convertPricePerUnit(
                        displayPriceMin.amount,
                        displayUnit,
                        stockUnit,
                        dims
                      ) ?? displayPriceMin.amount,
                      displayPriceMin.currency
                    )}
                  </span>
                  <span className="text-base text-gray-600 font-normal">-</span>
                  <span className="text-2xl text-neut-900 font-bold whitespace-nowrap">
                    {formatCurrency(
                      convertPricePerUnit(
                        displayPriceMax.amount,
                        displayUnit,
                        stockUnit,
                        dims
                      ) ?? displayPriceMax.amount,
                      displayPriceMax.currency
                    )}
                  </span>
                  <span
                    data-testid="price-unit"
                    className="text-base text-gray-600 font-normal whitespace-nowrap"
                  >
                    /{priceUnitLabel(shownUnit)}
                  </span>
                </div>
              ) : (
                <span className="text-2xl text-neut-900 font-bold">
                  {BLANK}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        <button
          onClick={onClick}
          className="w-full py-2.5 bg-prim text-white rounded-lg hover:bg-prim-hover transition-colors flex items-center justify-center gap-2 text-sm font-medium shadow-sm cursor-pointer"
        >
          <Eye size={16} aria-hidden="true" />
          {marketAvailability !== null
            ? t('product.compare_sellers', { count: sellersCount })
            : t('product.view_details')}
        </button>
      </div>
    </div>
  );
}

function getLeadTime(min: number, max: number): string {
  if (min === max) {
    return min.toString();
  }
  return `${min}-${max}`;
}
